import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { PaginatedResponse } from '@/types/api.types';

// API response wrapper type
interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

// API Incident response type
interface ApiIncident {
  assetIncidentId: number;
  assetId: string;
  assetDescription: string | null;
  userId: number;
  userName: string | null;
  locationId: number;
  locationName: string | null;
  incidentDate: string;
  incidentType: number;
  incidentTypeName: string | null;
  notes: string | null;
  resolved: boolean;
  resolvedDate: string | null;
  resolvedUserId: number | null;
  inventoryProcessId: number | null;
  imagesCount?: number;
  images?: ApiIncidentImage[];
}

interface ApiIncidentImage {
  imageId: number;
  imagePath: string;
  imageUrl: string | null;
  name: string | null;
  isPrimary?: boolean;
}

interface ApiIncidentType {
  incidentType: number;
  description: string;
}

export interface Incident {
  id: number;
  typeId: number;
  typeName: string;
  assetId: string;
  assetCode: string;
  assetName: string | null;
  description: string | null;
  priority: IncidentPriority;
  status: 'Open' | 'InProgress' | 'Resolved';
  resolution: string | null;
  resolvedBy: string | null;
  resolvedAt: string | null;
  createdBy: string;
  createdAt: string;
  locationId: number;
  locationName: string | null;
  images: IncidentImage[];
  imagesCount: number;
}

export interface IncidentImage {
  id: number;
  url: string;
  fileName: string;
}

export interface IncidentType {
  id: number;
  name: string;
  description: string;
}

export type IncidentStatus = 'Open' | 'InProgress' | 'Resolved';
export type IncidentPriority = 'Critical' | 'High' | 'Medium' | 'Low';

export interface IncidentSearchParams {
  search?: string;
  status?: string;
  priority?: string;
  resolved?: boolean;
  typeId?: number;
  assetId?: string;
  page?: number;
  pageSize?: number;
}

export interface CreateIncidentRequest {
  assetId: string;
  locationId: number;
  incidentType: number;
  notes?: string;
  inventoryProcessId?: number;
}

// Query keys
export const incidentKeys = {
  all: ['incidents'] as const,
  lists: () => [...incidentKeys.all, 'list'] as const,
  list: (params: IncidentSearchParams) => [...incidentKeys.lists(), params] as const,
  details: () => [...incidentKeys.all, 'detail'] as const,
  detail: (id: number) => [...incidentKeys.details(), id] as const,
  types: () => [...incidentKeys.all, 'types'] as const,
};

// Transform API Incident to our Incident interface
function transformIncident(apiInc: ApiIncident): Incident {
  return {
    id: apiInc.assetIncidentId,
    typeId: apiInc.incidentType,
    typeName: apiInc.incidentTypeName || `Type ${apiInc.incidentType}`,
    assetId: apiInc.assetId,
    assetCode: apiInc.assetId,
    assetName: apiInc.assetDescription,
    description: apiInc.notes,
    priority: 'Medium' as IncidentPriority,
    status: apiInc.resolved ? 'Resolved' : 'Open',
    resolution: null,
    resolvedBy: apiInc.resolvedUserId ? String(apiInc.resolvedUserId) : null,
    resolvedAt: apiInc.resolvedDate,
    createdBy: apiInc.userName || String(apiInc.userId),
    createdAt: apiInc.incidentDate,
    locationId: apiInc.locationId,
    locationName: apiInc.locationName,
    images: apiInc.images?.map(img => ({
      id: img.imageId,
      url: img.imageUrl || img.imagePath,
      fileName: img.name || `Image ${img.imageId}`,
    })) || [],
    imagesCount: apiInc.imagesCount || apiInc.images?.length || 0,
  };
}

// Search incidents
export function useIncidents(params: IncidentSearchParams) {
  return useQuery({
    queryKey: incidentKeys.list(params),
    queryFn: async () => {
      const response = await api.post<ApiResponse<{ data: ApiIncident[]; totalRecords: number }>>('/api/incident/search', {
        searchStr: params.search,
        resolved: params.resolved,
        page: params.page || 1,
        pageSize: params.pageSize || 25,
      });
      const apiData = response.data.data;
      return {
        items: apiData.data.map(transformIncident),
        totalCount: apiData.totalRecords,
        totalPages: Math.ceil(apiData.totalRecords / (params.pageSize || 25)),
        page: params.page || 1,
        pageSize: params.pageSize || 25,
      } as PaginatedResponse<Incident>;
    },
  });
}

// Get single incident
export function useIncident(id: number, enabled = true) {
  return useQuery({
    queryKey: incidentKeys.detail(id),
    queryFn: async () => {
      const response = await api.get<ApiResponse<ApiIncident>>('/api/incident/get', {
        params: { assetIncidentId: id },
      });
      return transformIncident(response.data.data);
    },
    enabled: enabled && id > 0,
  });
}

// Get incident types
export function useIncidentTypes() {
  return useQuery({
    queryKey: incidentKeys.types(),
    queryFn: async () => {
      const response = await api.get<ApiResponse<ApiIncidentType[]>>('/api/incident/types');
      return response.data.data.map(t => ({
        id: t.incidentType,
        name: t.description,
        description: t.description,
      }));
    },
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
  });
}

// Create incident
export function useCreateIncident() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateIncidentRequest) => {
      const response = await api.post<ApiResponse<number>>('/api/incident/save', {
        assetIncidentId: 0,
        assetId: data.assetId,
        locationId: data.locationId,
        incidentType: data.incidentType,
        notes: data.notes,
        inventoryProcessId: data.inventoryProcessId,
      });
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: incidentKeys.lists() });
    },
  });
}

// Update incident
export function useUpdateIncident() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<CreateIncidentRequest> }) => {
      const response = await api.post<ApiResponse<number>>('/api/incident/save', {
        assetIncidentId: id,
        assetId: data.assetId,
        locationId: data.locationId,
        incidentType: data.incidentType,
        notes: data.notes,
        inventoryProcessId: data.inventoryProcessId,
      });
      return response.data.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: incidentKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: incidentKeys.lists() });
    },
  });
}

// Resolve incident
export function useResolveIncident() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { id: number; resolution?: string }) => {
      const response = await api.post<ApiResponse<unknown>>('/api/incident/resolve', {
        assetIncidentId: data.id,
        resolution: data.resolution,
      });
      return response.data;
    },
    onSuccess: (_, data) => {
      queryClient.invalidateQueries({ queryKey: incidentKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: incidentKeys.lists() });
    },
  });
}

// Delete incident - Note: API doesn't have a delete endpoint
export function useDeleteIncident() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (_id: number) => {
      console.warn('Delete incident not implemented in API');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: incidentKeys.lists() });
    },
  });
}
